<script setup>
import { computed, useAttrs } from 'vue';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

import MessageMeta from '../MessageMeta.vue';
import MessageProviderBadge from '../MessageProviderBadge.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  contentAttributes,
} = useMessageContext();

const reactions = computed(() => contentAttributes.value?.reactions || []);

const groupedReactions = computed(() => {
  const groups = {};
  reactions.value.forEach(r => {
    if (r.emoji) groups[r.emoji] = (groups[r.emoji] || 0) + 1;
  });
  return groups;
});
const { t } = useI18n();

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  if (!inReplyTo.value?.id) return;
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="flex flex-col"
    :class="{ 'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL }"
  >
    <div class="text-sm" :class="messageClass" v-bind="attrs">
      <div
        v-if="inReplyTo"
        class="p-2 -mx-1 mb-2 rounded-lg bg-n-alpha-black1"
        :class="{ 'cursor-pointer': inReplyTo.id }"
        @click.stop="scrollToMessage"
      >
        <div
          v-dompurify-html="replyToPreview"
          class="prose prose-bubble line-clamp-2"
        />
      </div>
      <slot />
      <MessageMeta
        v-if="shouldShowMeta"
        :class="[
          flexOrientationClass,
          variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
          variant === MESSAGE_VARIANTS.PRIVATE
            ? 'text-n-amber-12/50'
            : 'text-n-slate-11',
        ]"
        class="mt-2"
      />
      <!-- Grouped messages hide the meta row; still show the per-message provider badge so a
           message sent via a different provider than its group-mate isn't left unmarked. -->
      <div v-else class="flex" :class="flexOrientationClass">
        <MessageProviderBadge class="mt-1" />
      </div>
    </div>
    <div
      v-if="Object.keys(groupedReactions).length"
      class="flex gap-1 mt-1 flex-wrap"
      :class="flexOrientationClass"
    >
      <span
        v-for="(count, emoji) in groupedReactions"
        :key="emoji"
        class="text-xs bg-n-alpha-1 border border-n-strong rounded-full px-2 py-0.5 flex items-center gap-1 cursor-default"
      >
        {{ emoji }}
        <span class="text-n-slate-11">{{ count }}</span>
      </span>
    </div>
  </div>
</template>
